import React, { useState, useEffect } from "react";
import { apiFetch } from "../utils/api";
import { toast } from "../components/Toast";
import LiveMap from "../components/LiveMap";
import MapErrorBoundary from "../components/MapErrorBoundary";

const Dashboard = () => {
  const [stats, setStats] = useState({
    vehicles: 0,
    activeTrips: 0,
    students: 0,
    routes: 0,
  });
  const [recentTrips, setRecentTrips] = useState([]);
  const [loading, setLoading] = useState(true);

  // Live GPS data keyed by vehicle_id
  const [liveVehicles, setLiveVehicles] = useState({});

  // Active trips are the primary identity — each has bus_number + vehicle_id
  const [activeTrips, setActiveTrips] = useState([]);
  const [selectedTripId, setSelectedTripId] = useState(null);
  const [scrollIndex, setScrollIndex] = useState(0);
  const [busSearch, setBusSearch] = useState("");

  const selectedTrip =
    activeTrips.find((t) => t.trip_id === selectedTripId) || null;

  // Filter trips by bus code search
  const filteredTrips = busSearch
    ? activeTrips.filter((t) =>
        (t.vehicle_code || t.bus_number || "")
          .toLowerCase()
          .includes(busSearch.toLowerCase()),
      )
    : activeTrips;

  const VISIBLE_COUNT = 8;
  const canScrollLeft = scrollIndex > 0;
  const canScrollRight = scrollIndex + VISIBLE_COUNT < filteredTrips.length;

  // Fetch dashboard data
  useEffect(() => {
    const loadDash = async () => {
      setLoading(true);
      try {
        const [vRes, tRes, sRes, rRes] = await Promise.all([
          apiFetch("/vehicles").catch(() => ({ data: [] })),
          apiFetch("/trips").catch(() => ({ data: [] })),
          apiFetch("/students").catch(() => ({ data: [] })),
          apiFetch("/routes").catch(() => ({ data: [] })),
        ]);

        const tripsList = tRes.data || [];
        const active = tripsList.filter((t) => t.status === "active");

        setActiveTrips(active);
        setRecentTrips(tripsList.slice(0, 5));

        // Auto-select first active trip or preserve previous active selection
        if (active.length > 0) {
          setSelectedTripId((prev) =>
            active.some((t) => t.trip_id === prev) ? prev : active[0].trip_id,
          );
        }

        // Clear stale live data for vehicles no longer active
        setLiveVehicles((prev) => {
          const activeVehicleIds = new Set(active.map((t) => t.vehicle_id));
          const next = {};
          for (const vid of Object.keys(prev)) {
            if (activeVehicleIds.has(vid)) next[vid] = prev[vid];
          }
          return next;
        });

        setStats({
          vehicles: (vRes.data || []).filter(
            (v) =>
              v.is_active === true || v.is_active === 1 || v.is_active === "1",
          ).length,
          activeTrips: active.length,
          students: (sRes.data || []).length,
          routes: (rRes.data || []).length,
        });
      } catch (e) {
        toast.error("Dashboard failed to load.");
      } finally {
        setLoading(false);
      }
    };
    loadDash();
  }, []);

  // Poll trips every 10s to keep active list fresh
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const tRes = await apiFetch("/trips");
        const tripsList = tRes.data || [];
        const active = tripsList.filter((t) => t.status === "active");
        setActiveTrips(active);
        setRecentTrips(tripsList.slice(0, 5));
        setStats((prev) => ({ ...prev, activeTrips: active.length }));
        setLiveVehicles((prev) => {
          const activeVehicleIds = new Set(active.map((t) => t.vehicle_id));
          const next = {};
          for (const vid of Object.keys(prev)) {
            if (activeVehicleIds.has(vid)) next[vid] = prev[vid];
          }
          return next;
        });
      } catch (e) {
        /* silent */
      }
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  // Poll REST API for live GPS data — more reliable than socket.io
  useEffect(() => {
    if (!selectedTrip) return; // Only poll if a trip is selected

    const interval = setInterval(async () => {
      try {
        const gpsRes = await apiFetch(`/gps/live/${selectedTrip.vehicle_id}`);
        const gpsData = gpsRes?.data ?? gpsRes;
        if (
          gpsData &&
          gpsData.latitude !== undefined &&
          gpsData.longitude !== undefined
        ) {
          console.log("[Dashboard] GPS data received from REST API:", gpsData);
          setLiveVehicles((prev) => ({
            ...prev,
            [selectedTrip.vehicle_id]: gpsData,
          }));
        }
      } catch (e) {
        // Silent catch - GPS data not available yet
      }
    }, 1000); // Poll every 1 second for real-time updates

    return () => clearInterval(interval);
  }, [selectedTrip]);

  const MetricCard = ({ title, val, icon }) => (
    <div
      className="glass-panel"
      style={{
        padding: "24px",
        display: "flex",
        alignItems: "center",
        gap: "20px",
      }}
    >
      <div style={{ fontSize: "2.5rem" }}>{icon}</div>
      <div>
        <div
          style={{
            color: "var(--text-muted)",
            fontSize: "0.9rem",
            textTransform: "uppercase",
            letterSpacing: "1px",
          }}
        >
          {title}
        </div>
        <div style={{ fontSize: "2.5rem", fontWeight: "800", color: "#000" }}>
          {loading ? "-" : val}
        </div>
      </div>
    </div>
  );

  const liveData = selectedTrip
    ? liveVehicles[selectedTrip.vehicle_id] || null
    : null;

  return (
    <div>
      <h1 className="page-title text-gradient" style={{ marginBottom: "32px" }}>
        Operational Overview
      </h1>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: "24px",
          marginBottom: "32px",
        }}
      >
        <MetricCard title="Total Vehicles" val={stats.vehicles} icon="🚌" />
        <MetricCard title="Active Trips" val={stats.activeTrips} icon="⚡" />
        <MetricCard title="Students" val={stats.students} icon="🎒" />
        <MetricCard title="Routes" val={stats.routes} icon="🗺️" />
      </div>

      {/* LIVE FLEET TRACKING */}
      <div
        className="glass-panel"
        style={{
          padding: "24px",
          marginBottom: "32px",
          border: "1px solid var(--border-color)",
        }}
      >
        <h3
          style={{
            borderBottom: "1px solid var(--border-color)",
            paddingBottom: "16px",
            marginBottom: "24px",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <span>🔴 Live Fleet Tracking</span>
          <span style={{ fontSize: "0.9rem", color: "var(--text-muted)" }}>
            {activeTrips.length} Active Bus
            {activeTrips.length !== 1 ? "es" : ""}
          </span>
        </h3>

        {activeTrips.length === 0 ? (
          <p
            style={{
              color: "var(--text-muted)",
              textAlign: "center",
              padding: "32px 16px",
            }}
          >
            No active trips running right now.
          </p>
        ) : (
          <div>
            {/* BUS CODE SEARCH */}
            <div style={{ marginBottom: "16px" }}>
              <input
                type="text"
                placeholder="Search by bus code..."
                value={busSearch}
                onChange={(e) => {
                  setBusSearch(e.target.value);
                  setScrollIndex(0);
                }}
                style={{
                  borderRadius: "6px",
                  border: "1px solid var(--border-color)",
                  background: "var(--bg-secondary)",
                  color: "var(--text-primary)",
                  width: "200px",
                  height: "40px",
                }}
              />
            </div>

            {/* BUS SELECTOR with scroll arrows */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                marginBottom: "24px",
              }}
            >
              {canScrollLeft && (
                <button
                  onClick={() =>
                    setScrollIndex((prev) => Math.max(0, prev - 1))
                  }
                  style={{
                    padding: "8px 12px",
                    borderRadius: "6px",
                    border: "1px solid var(--border-color)",
                    background: "var(--bg-secondary)",
                    cursor: "pointer",
                    fontSize: "1.2rem",
                  }}
                >
                  ←
                </button>
              )}

              <div style={{ display: "flex", gap: "10px", overflow: "hidden" }}>
                {filteredTrips
                  .slice(scrollIndex, scrollIndex + VISIBLE_COUNT)
                  .map((trip) => (
                    <div
                      key={trip.trip_id}
                      className={`bus-icon active-trip${selectedTripId === trip.trip_id ? " selected" : ""}`}
                      onClick={() => setSelectedTripId(trip.trip_id)}
                      style={{ minWidth: "80px" }}
                    >
                      <div style={{ fontWeight: 700, fontSize: "0.9rem" }}>
                        {trip.vehicle_code || trip.bus_number || "—"}
                      </div>
                      <div
                        style={{
                          fontSize: "0.7rem",
                          opacity: 0.75,
                          marginTop: "2px",
                        }}
                      >
                        {trip.registration_no || "—"}
                      </div>
                    </div>
                  ))}
              </div>

              {canScrollRight && (
                <button
                  onClick={() => setScrollIndex((prev) => prev + 1)}
                  style={{
                    padding: "8px 12px",
                    borderRadius: "6px",
                    border: "1px solid var(--border-color)",
                    background: "var(--bg-secondary)",
                    cursor: "pointer",
                    fontSize: "1.2rem",
                  }}
                >
                  →
                </button>
              )}
            </div>

            {/* SELECTED BUS DETAIL + MAP */}
            {selectedTrip ? (
              <div>
                {/* Header bar */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "12px 0",
                    marginBottom: "12px",
                    borderBottom: "1px solid var(--border-color)",
                  }}
                >
                  <div>
                    <span style={{ fontWeight: 700, fontSize: "1.05rem" }}>
                      {selectedTrip.vehicle_code ||
                        selectedTrip.bus_number ||
                        "—"}
                    </span>
                    <span
                      style={{
                        color: "var(--text-muted)",
                        marginLeft: "10px",
                        fontSize: "0.9rem",
                      }}
                    >
                      Vehicle: {selectedTrip.registration_no || "—"}
                    </span>
                    <span
                      style={{
                        color: "var(--text-muted)",
                        marginLeft: "10px",
                        fontSize: "0.9rem",
                      }}
                    >
                      Driver: {selectedTrip.driver_name || "—"}
                    </span>
                    <span
                      style={{
                        color: "var(--text-muted)",
                        marginLeft: "10px",
                        fontSize: "0.9rem",
                      }}
                    >
                      Phone: {selectedTrip.driver_mobile || "—"}
                    </span>
                    <span
                      style={{
                        color: "var(--text-muted)",
                        marginLeft: "10px",
                        fontSize: "0.9rem",
                      }}
                    >
                      Route: {selectedTrip.route_name || "—"}
                    </span>
                  </div>
                  <span
                    style={{
                      color: "var(--success)",
                      fontWeight: 600,
                      fontSize: "0.85rem",
                    }}
                  >
                    🟢 Active
                  </span>
                </div>

                {/* Map */}
                <MapErrorBoundary vehicleId={selectedTrip.vehicle_id}>
                  <LiveMap
                    key={selectedTrip.vehicle_id}
                    vehicleId={selectedTrip.vehicle_id}
                    liveData={liveData}
                  />
                </MapErrorBoundary>

                {/* Live stats */}
                {liveData && (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(160px, 1fr))",
                      gap: "12px",
                      marginTop: "16px",
                    }}
                  >
                    <div
                      style={{
                        background: "var(--bg-alt)",
                        padding: "12px",
                        borderRadius: "8px",
                        border: "1px solid var(--border-color)",
                      }}
                    >
                      <div
                        style={{
                          color: "var(--text-muted)",
                          fontSize: "0.8rem",
                        }}
                      >
                        📡 Coordinates
                      </div>
                      <div
                        style={{
                          fontFamily: "monospace",
                          fontSize: "0.85rem",
                          marginTop: "4px",
                        }}
                      >
                        {Number(liveData.latitude).toFixed(4)},{" "}
                        {Number(liveData.longitude).toFixed(4)}
                      </div>
                    </div>
                    <div
                      style={{
                        background: "var(--bg-alt)",
                        padding: "12px",
                        borderRadius: "8px",
                        border: "1px solid var(--border-color)",
                      }}
                    >
                      <div
                        style={{
                          color: "var(--text-muted)",
                          fontSize: "0.8rem",
                        }}
                      >
                        ⏩ Speed
                      </div>
                      <div style={{ fontSize: "0.85rem", marginTop: "4px" }}>
                        {liveData.speed || 0} km/h
                      </div>
                    </div>
                    <div
                      style={{
                        background: "var(--bg-alt)",
                        padding: "12px",
                        borderRadius: "8px",
                        border: "1px solid var(--border-color)",
                      }}
                    >
                      <div
                        style={{
                          color: "var(--text-muted)",
                          fontSize: "0.8rem",
                        }}
                      >
                        🕐 Last Update
                      </div>
                      <div style={{ fontSize: "0.85rem", marginTop: "4px" }}>
                        {new Date(liveData.timestamp).toLocaleTimeString()}
                      </div>
                    </div>
                  </div>
                )}
                {!liveData && (
                  <p
                    style={{
                      color: "var(--text-muted)",
                      marginTop: "12px",
                      fontSize: "0.9rem",
                    }}
                  >
                    Waiting for GPS data...
                  </p>
                )}
              </div>
            ) : null}
          </div>
        )}
      </div>

      {/* RECENT TRIPS */}
      <div className="glass-panel" style={{ padding: "24px" }}>
        <h3
          style={{
            borderBottom: "1px solid var(--border-color)",
            paddingBottom: "16px",
            marginBottom: "16px",
          }}
        >
          Recent Trips
        </h3>
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {loading ? (
            <p>Loading...</p>
          ) : recentTrips.length === 0 ? (
            <p style={{ color: "var(--text-muted)" }}>No trips yet.</p>
          ) : (
            recentTrips.map((t) => (
              <div
                key={t.trip_id}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "12px",
                  background: "var(--bg-alt)",
                  borderRadius: "8px",
                }}
              >
                <div>
                  <div style={{ fontWeight: 600 }}>{t.bus_number || "—"}</div>
                  <div
                    style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}
                  >
                    {t.route_name || t.route_id?.substring(0, 8)} ·{" "}
                    {t.driver_name || "No driver"}
                  </div>
                </div>
                <span
                  style={{
                    fontSize: "0.8rem",
                    fontWeight: 600,
                    color:
                      t.status === "active"
                        ? "var(--success)"
                        : "var(--text-muted)",
                    textTransform: "uppercase",
                    alignSelf: "center",
                  }}
                >
                  {t.status}
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
